/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useTranslations } from "@i18n/react";
import type {
  ChangeEventHandler,
  Dispatch,
  JSX,
  RefObject,
  SetStateAction,
} from "react";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useFocus } from "react-aria";

import type { ThemeType } from "../../../../../global/emotion";
import {
  colors,
  numericValues,
  radius,
  spacing,
  stroke,
} from "../../../../../global/emotion";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import { determineChildrenInject } from "../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";
import type { IconElement } from "../../icons/Icon/Icon";
import { ArrowLeftIcon } from "../../icons/Icon/NavigationIcons";
import { Button } from "../Button/Button.emotion";
import { IconButton } from "../IconButton/IconButton.emotion";
import { Text } from "../Text/Text.emotion";

const hallowElement: HallowElement = "input";

const asChildStyle: Interpolation = {
  backgroundColor: colors.transparent,
  flexGrow: 1,
};
const baseStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
};
const buttonInvisibleStyle: Interpolation = {
  visibility: "hidden",
};
const buttonVisibleStyle: Interpolation = {
  visibility: "visible",
};
const disabledStyle: Interpolation = {
  opacity: numericValues["0.5"],
};
const disabledCursorStyle: Interpolation = {
  cursor: "not-allowed",
};
const divStyle: Interpolation<ThemeType> = (theme) => ({
  alignItems: "center",
  borderColor: theme.semanticColors.neutralsLowest,
  borderRadius: radius.full,
  borderStyle: "solid",
  borderWidth: stroke.regular,
  color: theme.semanticColors.neutralsMedium,
  cursor: "text",
  display: "flex",
  gap: spacing.xxs,
  height: spacing.xxl,
  paddingLeft: spacing.ms,
  paddingRight: spacing.ms,
  width: spacing.full,
});
const divIsFocusedStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.neutralsLowest,
  color: theme.semanticColors.primary,
});
const iconButtonStyle: Interpolation = {
  height: spacing.xxl,
  minWidth: spacing.xxl,
  width: spacing.xxl,
};
const inputStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: colors.transparent,
  border: "none",
  color: theme.semanticColors.primary,
  flexGrow: 1,
  height: spacing.full,
  outline: "none",
  padding: spacing.none,
});

export type InputProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  clearable?: boolean;
  closeable?: boolean;
  icon?: IconElement;
  containerCSS?: Interpolation;
  inputWrapperCSS?: Interpolation;
  loseFocusOnOutsideClick?: boolean;
  onClose?: () => void;
  onFocusChange?: (isFocused: boolean) => void;
  stateSetter?: Dispatch<SetStateAction<any>>;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      asChild = false,
      children,
      clearable,
      closeable,
      disabled,
      icon,
      containerCSS,
      inputWrapperCSS,
      loseFocusOnOutsideClick = true,
      onChange,
      onClose,
      onFocusChange,
      stateSetter,
      value,
      ...props
    },
    ref,
  ) => {
    const internalInputRef = (ref ??
      useRef<HTMLInputElement>(undefined)) as RefObject<HTMLInputElement>;

    const [isFocused, setIsFocused] = useState(false);
    const [internalState, setInternalState] = useState(value ?? "");

    const { focusProps } = useFocus({
      onFocusChange: (isFocused) => {
        if (isFocused || (!isFocused && loseFocusOnOutsideClick)) {
          setIsFocused(isFocused);
          if (onFocusChange) onFocusChange(isFocused);
        }
      },
    });

    const t = useTranslations();

    const handleInternalStateChange: ChangeEventHandler<HTMLInputElement> = (
      event,
    ) => {
      event.preventDefault();
      setInternalState(event.target.value);
      if (stateSetter) stateSetter(event.target.value);
      if (onChange) onChange(event);
    };

    const handleClearable = () => {
      handleInternalStateEmpty();
      setTimeout(() => setInternalInputRefFocus(), 1);
    };

    const handleCloseable = () => {
      handleInternalStateEmpty();
      setInternalInputRefBlur();
      onClose?.();
    };

    const handleInternalStateEmpty = () => {
      setInternalState("");
      if (stateSetter) stateSetter("");
    };

    const setInternalInputRefBlur = () => {
      internalInputRef.current.blur();
      setIsFocused(false);
      if (onFocusChange) onFocusChange(false);
    };

    const setInternalInputRefFocus = () => {
      if (!disabled) internalInputRef.current.focus();
    };

    useEffect(() => {
      setInternalState(value ?? "");
    }, [value]);

    if (asChild) {
      const DeterminedElement = determineElementFromAsChild({
        asChild,
        hallowElement,
      });
      const DeterminedChildren = determineChildrenInject({
        afterChildrenInject: null,
        beforeChildrenInject: [icon ? icon : null],
        children: children as JSX.Element,
      });
      return (
        <DeterminedElement
          ref={ref}
          css={[
            asChildStyle,
            divStyle,
            disabled && [disabledStyle, disabledCursorStyle],
          ]}
          {...props}
        >
          {DeterminedChildren}
        </DeterminedElement>
      );
    }

    return (
      <div
        ref={ref}
        css={[
          baseStyle,
          disabled && [disabledStyle, disabledCursorStyle],
          containerCSS,
        ]}
      >
        {closeable && isFocused && (
          <IconButton
            onClick={() => handleCloseable()}
            icon={<ArrowLeftIcon />}
            css={iconButtonStyle}
            variant="neutral"
          />
        )}
        <div
          onClick={setInternalInputRefFocus}
          css={[
            divStyle,
            disabled && disabledCursorStyle,
            isFocused && divIsFocusedStyle,
            inputWrapperCSS,
          ]}
        >
          {icon && icon}
          <Text asChild overflow="ellipsis" size="m" type="body">
            <input
              disabled={disabled}
              onChange={handleInternalStateChange}
              ref={internalInputRef}
              size={1}
              value={internalState}
              css={[inputStyle, disabled && disabledCursorStyle]}
              {...focusProps}
              {...props}
            />
          </Text>
          {clearable && (
            <Button
              onMouseDown={handleClearable}
              size="xs"
              css={
                internalState && internalState !== ""
                  ? buttonVisibleStyle
                  : buttonInvisibleStyle
              }
              variant="transparentNeutral"
            >
              {t("web_words_clear")}
            </Button>
          )}
        </div>
      </div>
    );
  },
);

Input.displayName = "Input";
